import { getErrorMessage } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import type { PositionsProduct } from "~/v3-poc/api/positions";
import {
  useClosePositions,
  useGetPositions,
} from "~/v3-poc/hooks/useGetPositions";
import { useInvalidate } from "~/v3-poc/kernel/react/Invalidation.context";

export interface UseLiquidateAllPositionsArgs {
  accountID?: string;
  product?: PositionsProduct;
  /** Skip the positions read until the dialog is actually open. */
  enabled?: boolean;
}

export interface LiquidateAllPositionsState {
  positionCount: number;
  isLoadingPositions: boolean;
  /** A failed read normalises to `[]`, which must not read as an empty book. */
  positionsErrorMessage?: string;
  onRetryPositions: () => void;
  isPending: boolean;
  isSuccess: boolean;
  errorMessage?: string;
  /** Count captured when the request was sent, so the success copy stays true. */
  liquidatedCount: number;
  liquidate: (options: { cancelOrders: boolean }) => void;
  reset: () => void;
}

/**
 * Liquidate-all for the risk-controls dialog. Deliberately independent of the
 * positions widget: it owns its own read of the book so the action works from
 * the modal whether or not that widget is on the workspace.
 */
export const useLiquidateAllPositions = ({
  accountID,
  product,
  enabled = true,
}: UseLiquidateAllPositionsArgs): LiquidateAllPositionsState => {
  const invalidate = useInvalidate();
  const isReady = Boolean(accountID) && Boolean(product);

  const {
    positions,
    isLoading,
    isError,
    error: positionsError,
    refetch,
  } = useGetPositions({
    accountID,
    product,
    options: { enabled: enabled && isReady },
  });

  const mutation = useClosePositions({
    // The dialog renders the failure itself, so the global toast would double it.
    onError: () => undefined,
    onSuccess: () => {
      invalidate({ queryKey: queryKeys.positions.all });
      invalidate({ queryKey: queryKeys.orders.all });
    },
  });

  const symbols = positions.map((position) => position.symbol);

  const liquidate = ({ cancelOrders }: { cancelOrders: boolean }) => {
    if (!accountID || !product || symbols.length === 0) {
      return;
    }
    mutation.mutate({ accountID, product, symbols, cancelOrders });
  };

  return {
    positionCount: positions.length,
    isLoadingPositions: enabled && isReady && isLoading,
    positionsErrorMessage: isError
      ? getErrorMessage(positionsError) || "Couldn’t load your open positions."
      : undefined,
    onRetryPositions: () => {
      void refetch();
    },
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    errorMessage: mutation.error ? getErrorMessage(mutation.error) : undefined,
    liquidatedCount: mutation.variables?.symbols.length ?? 0,
    liquidate,
    reset: mutation.reset,
  };
};
